import { booleanAttribute, ChangeDetectionStrategy, Component, computed, effect, input, linkedSignal, signal, untracked } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { CarouselTile, SwipeStart } from './carousel.model';
import { NgOptimizedImage } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';


const AUTOPLAY_TIME_THRESHOLD = 15000; // 3000; 

/**
 * A declarative, reactive carousel with unlimited number of tiles using modern animation.
 */
@Component({
  selector: 'app-carousel',
  imports: [
    NgOptimizedImage,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './carousel.component.html',
  styleUrl: './carousel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselComponent {
  readonly tiles = input.required<CarouselTile[]>();
  // Carousel-specific configuration
  readonly repo = input<string | null | undefined>(null);
  readonly quality = input<number>(60);
  readonly useFill = input<boolean>(true);
  readonly usePlaceholder = input<boolean>(true);
  readonly disablePagination = input(false, { transform: booleanAttribute })
  // readonly items = signal([
  //   { id: 1, url: 'A' },
  //   { id: 2, url: 'B' },
  //   { id: 3, url: 'C' },
  // ]);
  protected readonly items = linkedSignal(() => {
    const tiles = this.tiles();
    return tiles.map((tile, idx) => ({
      ...tile,
      id: crypto.randomUUID(),
      idx,
      sizes: tile.img?.sizes?.join(',')
    }));
  });

  protected readonly isAnimating = signal(false);
  protected readonly offset = signal(0);
  protected readonly transformStyle = computed(() => `translateX(${this.offset()}%)`);
  protected readonly autoPlayEnabled = signal(true); // Toggle for auto-slide

  protected readonly currentIndex = computed(() => this.items()[0].idx);

  /**
   * Swipe status
   */
  private _lastSwipeStart: SwipeStart | null = null;


  constructor() {
    effect((onCleanup) => {
      const autoPlayEnabled = this.autoPlayEnabled();
      const isAnimating = this.isAnimating();
      // We only care if autoplay is on AND we are not currently moving
      if (autoPlayEnabled && !isAnimating) {

        const timer = setTimeout(() => {
          // Double check we are still not animating before triggering
          untracked(() => {
            this._next();
          })

        }, AUTOPLAY_TIME_THRESHOLD);
        // This is vital: it kills the timer if the user clicks
        // 'Pause' or 'Prev' manually during the 3s wait.
        onCleanup(() => clearTimeout(timer));
      }
    });
  }
  /** technical, internal method to move to next tile */
  private _next(): void {
    if (this.isAnimating()) return;

    this.isAnimating.set(true);
    this.offset.set(-100);
  }
  /** user initiated click on next button */
  protected moveNext(ev?: Event): void {
    ev?.stopPropagation();
    this._next();
    this.autoPlayEnabled.set(false);
  }

  protected movePrev(ev?: Event): void {
    ev?.stopPropagation();
    if (this.isAnimating()) return;


    // 1. Prepare DOM and Position (No Animation)
    this.isAnimating.set(false);
    this.items.update(list => [list[list.length - 1], ...list.slice(0, -1)]);
    this.offset.set(-100);

    // 2. Trigger Slide (Wait for Paint)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.isAnimating.set(true);
        this.offset.set(0);
        this.autoPlayEnabled.set(false);
      });
    });
  }

  /**
   * swipe handler
   */
  swipe(e: TouchEvent, what: string): void {
    const coord: [number, number] = [e.changedTouches[0].clientX, e.changedTouches[0].clientY];
    const time = new Date().getTime();
    if (what === 'start') {
      this._lastSwipeStart = { coord, time };
    } else if (what === 'end' && this._lastSwipeStart) {
      const direction = [coord[0] - this._lastSwipeStart.coord[0], coord[1] - this._lastSwipeStart.coord[1]];
      const duration = time - this._lastSwipeStart.time;
      if (duration < 1000 //
        && Math.abs(direction[0]) > 30 // Long enough
        && Math.abs(direction[0]) > Math.abs(direction[1] * 3)) { // Horizontal enough
        const swipe = direction[0] < 0 ? 'next' : 'previous';
        // tslint:disable-next-line: no-console
        //this.logService.info(swipe);
        if (swipe === 'next') {
          this.moveNext();
        } else if (swipe === 'previous') {
          this.movePrev();
        }
      }
    }
  }

  protected toggleAutoPlay(ev: Event) {
    ev?.stopPropagation();
    // If we are about to start playing...
    if (!this.autoPlayEnabled()) {
      // 1. Immediately trigger the next slide
      this._next();

      // 2. Then enable the auto-play signal
      this.autoPlayEnabled.set(true);
    } else {
      // Just pause if it's currently playing
      this.autoPlayEnabled.set(false);
    }
  }

  /**
   * Navigate to a specific item by its index.
   * @param targetIdx The index of the item to navigate to (based on original tile position)
   * @param disableAutoPlay Whether to disable autoplay after navigation (default: true)
   */
  protected goToIndex(ev: Event, targetIdx: number, disableAutoPlay = true): void {
    ev?.stopPropagation();
    if (this.isAnimating()) return;

    const currentIdx = this.currentIndex();
    const totalItems = this.items().length;

    // Validate target index
    if (targetIdx < 0 || targetIdx >= totalItems || targetIdx === currentIdx) {
      return;
    }

    // Calculate the shortest path (forward or backward)
    const forwardDistance = (targetIdx - currentIdx + totalItems) % totalItems;
    const backwardDistance = (currentIdx - targetIdx + totalItems) % totalItems;

    // Perform instant rotation without animation
    this.isAnimating.set(false);

    if (forwardDistance <= backwardDistance) {
      // Move forward
      const rotations = forwardDistance;
      this.items.update(list => {
        const result = [...list];
        for (let i = 0; i < rotations; i++) {
          result.push(result.shift()!);
        }
        return result;
      });
    } else {
      // Move backward
      const rotations = backwardDistance;
      this.items.update(list => {
        const result = [...list];
        for (let i = 0; i < rotations; i++) {
          result.unshift(result.pop()!);
        }
        return result;
      });
    }

    this.offset.set(0);

    if (disableAutoPlay) {
      this.autoPlayEnabled.set(false);
    }
  }

  finalizeMove() {
    //console.log('Transition ended. Offset was:', this.offset());
    this.isAnimating.set(false); // ALWAYS set this to false first to unlock the slider

    if (this.offset() === -100) {
      // Logic for Forward Move: Rotate array and reset offset
      this.items.update(([first, ...rest]) => [...rest, first]);
      this.offset.set(0);
    }
    // If offset is 0 (Backward move finished), we don't need to do anything 
    // because movePrev already reordered the array.
    //console.log('Transition ended. items:', this.items());
  }
}

/*
Since you'll be swapping in real images later, just a final tip: 
make sure your images have a consistent aspect ratio or use object-fit: cover in your CSS. 
It prevents the slider from "jumping" in height if one image happens to be taller than the others.

Whenever you're ready to tackle the next part—be it lazy loading those images or adding swipe support
—I'll be here.
*/
